// @flow
import React, { Component } from 'react';
import { themr } from 'react-css-themr';
import { Route, Switch, Redirect, type Match } from 'react-router-dom';
import { translate } from 'react-i18next';
import { connect } from 'react-redux';
import { compose } from 'redux';

import { RouteNotFound, withRouter } from 'common/components/core/router';
import { Loader } from 'common/components/core/loader/Loader';
import { Title } from 'common/components/common';

import { EntityMgmtHeader } from 'common/containers/AppHeaderContainer';

import {
    personnelBUsSelector,
    getSelectedBusinessUnit
} from 'common/redux/selectors/intl';
import {
    personnelSelector,
    personnelIdSelector,
    isGlobalPersonnelSelector
} from 'common/redux/selectors/sessionSelector';
import {
    getDefaultVisibleSection,
    getSectionVisibilitySettings
} from 'common/redux/selectors/personnelSelectors';
import { getPlatformSettings } from 'common/modules/platform-settings';
import { selectIsChatVisible } from 'common/modules/chat';

import { SectionVisibilitySettings } from 'common/models/internal/Client';
import theme from './ClientRoutesContainer.scss';
import { clientSelector } from 'common/redux/selectors/queryParamsSelectors';
import LeftNavigationBar from 'container/components/navigation/LeftBar';
import { Columns, Column, Box } from '@locus-taxy/neo-ui';
import SectionWrap from 'common/components/common/SectionWrap';
import { ErrorBoundary } from 'common/components/core/errors';
import getPlatformRoutes from './platform';
import getTrackIQRoutes from './trackIQ';
import getDispatchIQRoutes from './dispatchIQ';
import getOrderIQRoutes from './orderIQ';
import getNetOptRoutes from './netOpt';
import getFieldProRoutes from './fieldPro';
import getSettingsRoutes from './settings';
import getOrchestratorRoutes from './orchestrator';
import ExpiryBanner from 'container/components/ExpiryBanner';

const EntityManagementContainer = React.lazy(() =>
    import(
        /* webpackChunkName: "entityManagement" */
        /* webpackPrefetch: true */ 'container/containers/entity/EntityManagementContainer'
    )
);
const ParameterizedEntityManagementSection = translate()(props => {
    return (
        <SectionWrap>
            <Title i18nKey="masterDashboard" />
            <EntityMgmtHeader {...props} />
            <React.Suspense fallback={<Loader active />}>
                <EntityManagementContainer {...props} />
            </React.Suspense>
        </SectionWrap>
    );
});

const ChatModule = React.lazy(() =>
    import(
        /* webpackChunkName: "chat-container" */
        /* webpackPrefetch: true */ 'common/modules/chat'
    )
);

const ChatContainer = ({ isChatVisible }) => (
    <React.Suspense fallback={null}>
        {isChatVisible && <ChatModule />}
    </React.Suspense>
);

type ClientRoutesProps = {
    defaultVisibleSection: string,
    match: Match,
    isGlobalPersonnel: boolean,
    visibleSections: SectionVisibilitySettings,
    isChatVisible: boolean
};

class ClientRoutesContainer extends Component<ClientRoutesProps> {
    getDefaultLandingSection = (): string => {
        const { defaultVisibleSection, match } = this.props;
        const { clientId } = match.params;

        if (!clientId) {
            throw 'Client Id is not defined';
        }

        return `/client/${clientId}/${defaultVisibleSection}`;
    };

    renderRoutes() {
        const { props } = this;
        const { match, visibleSections, isChatVisible } = props;

        const platformRoutes = getPlatformRoutes(props);
        const trackIQRoutes = getTrackIQRoutes(props);
        const dispatchIQRoutes = getDispatchIQRoutes(props);
        const orderIQRoutes = getOrderIQRoutes(props);
        const netOptRoutes = getNetOptRoutes(props);
        const fieldProRoutes = getFieldProRoutes(props);
        const settingsRoutes = getSettingsRoutes(props);
        const orchestratorRoutes = getOrchestratorRoutes(props);

        return (
            <ErrorBoundary>
                <Switch>
                    {platformRoutes}
                    {trackIQRoutes}
                    {dispatchIQRoutes}
                    {orderIQRoutes}
                    {netOptRoutes}
                    {fieldProRoutes}
                    {orchestratorRoutes}

                    {/* Settings Section */}
                    {settingsRoutes}

                    {/* Entity Management */}
                    {visibleSections.showEntitySection && (
                        <Route
                            path={`${match.path}/entity`}
                            component={ParameterizedEntityManagementSection}
                        />
                    )}
                    <Redirect
                        exact
                        from={match.path}
                        to={this.getDefaultLandingSection()}
                    />
                    {/*Temporary redirect to support fallback*/}
                    <Redirect
                        exact
                        from={`${match.path}/orchestration`}
                        to={`${match.url}/shipflex`}
                    />
                    {/* 404 */}
                    <RouteNotFound />
                </Switch>
                <ChatContainer isChatVisible={isChatVisible} />
            </ErrorBoundary>
        );
    }

    render() {
        const { match } = this.props;
        const { clientId } = match.params;
        return (
            <>
                <ExpiryBanner />
                <Columns height="fluid" alignY="stretch">
                    <LeftNavigationBar clientId={clientId} />
                    <Column>
                        {/* hack, adds div for regression */}
                        <Box height="fluid">{this.renderRoutes()}</Box>
                    </Column>
                </Columns>
            </>
        );
    }
}

const mapStateToProps = (state, props) => {
    const clientId = clientSelector(state, props);
    const businessUnits = personnelBUsSelector(state, props);
    const personnelId = personnelIdSelector(state);

    const visibleSections = getSectionVisibilitySettings(state, props);
    const isGlobalPersonnel = isGlobalPersonnelSelector(state, props);
    const personnel = personnelSelector(state);
    const selectedBU = getSelectedBusinessUnit(state, props);
    const defaultVisibleSection = getDefaultVisibleSection(state, props);
    const { alertsSettings } = getPlatformSettings();
    const isChatVisible = selectIsChatVisible(state, props);

    return {
        clientId,
        businessUnits,
        selectedBU,
        personnelId,
        personnel,
        isGlobalPersonnel,
        defaultVisibleSection,
        visibleSections,
        alertsSettings,
        isChatVisible
    };
};

const mapDispatchToProps = {};

export default (compose(
    withRouter,
    connect(mapStateToProps, mapDispatchToProps),
    translate(),
    themr('ClientRoutesContainer', theme)
)(ClientRoutesContainer): any);
